<template>
  <div class="pa-1 mt-4">
    <!-- Desktop View -->
    <v-data-table
      v-if="!device.isMobile"
      :headers="headers"
      :items="restaurantSummary"
      class="striped-table ordera-box-shadow"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
      :loading="loading"
    >
      <template v-slot:no-data>
        <v-container class="text-center py-12">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-store-off</v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("no_data") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("select_date_and_filters") }}
          </div>
        </v-container>
      </template>

      <template v-slot:item.num="{ item }">
        {{ item.num }}
      </template>

      <template v-slot:item.restaurant="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" :color="item.isTotal ? 'primary' : 'orange'">
            {{ item.isTotal ? "mdi-sigma" : "mdi-store" }}
          </v-icon>
          <span :class="item.isTotal ? 'font-weight-bold' : 'font-weight-medium'">
            {{ toLatin(item.restaurant) }}
          </span>
        </div>
      </template>

      <template v-slot:item.meal_count="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ item.meal_count }}
        </span>
      </template>

      <template v-slot:item.total_amount="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ formatCurrency(item.total_amount) }}
        </span>
      </template>

      <template v-slot:item.company_amount="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ formatCurrency(item.company_amount) }}
        </span>
      </template>

      <template v-slot:item.employee_amount="{ item }">
        <span :class="item.isTotal ? 'font-weight-bold' : ''">
          {{ formatCurrency(item.employee_amount) }}
        </span>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="restaurantSummary"
      :loading="loading"
      item-key="id"
      :fields-per-row="2"
      :bold-fields="['restaurant']"
      :show-row-number="false"
      class="px-3"
    >
      <template v-slot:item.restaurant="{ item }">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" :color="item.isTotal ? 'primary' : 'orange'">
            {{ item.isTotal ? "mdi-sigma" : "mdi-store" }}
          </v-icon>
          {{ toLatin(item.restaurant) }}
        </div>
      </template>
      <template v-slot:item.total_amount="{ item }">
        {{ formatCurrency(item.total_amount) }}
      </template>
      <template v-slot:item.company_amount="{ item }">
        {{ formatCurrency(item.company_amount) }}
      </template>
      <template v-slot:item.employee_amount="{ item }">
        {{ formatCurrency(item.employee_amount) }}
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyMultiRestaurantTabRestaurantSummary",
  components: {
    MobileDataCards,
  },
  mixins: [hasSnackbar, hasPrinting, hasReportPrinting, hasExcel],
  data: () => ({
    loading: false,
  }),
  computed: {
    filters() {
      return this.$store.state.report.filters;
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    restaurantSummary() {
      // Generate fake data for selected restaurants based on date range
      const summary = [];
      let totalMeals = 0;
      let totalAmount = 0;
      let totalCompanyAmount = 0;
      let totalEmployeeAmount = 0;

      // Calculate number of days in selected date range
      const dateFrom = this.filters.dates?.from;
      const dateTo = this.filters.dates?.to;
      let dayCount = 1;

      if (dateFrom && dateTo) {
        const start = new Date(dateFrom);
        const end = new Date(dateTo);
        dayCount = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1);
      }

      this.selectedRestaurants.forEach((restaurantId, index) => {
        const restaurant = this.restaurants.find((r) => r.id === restaurantId);
        if (!restaurant) return;

        // Generate random data based on seed from restaurant ID and dates for consistency
        const seed = restaurantId + (dateFrom || "").length + (dateTo || "").length;
        const randomFactor = (Math.sin(seed) + 1) / 2; // 0-1

        // Calculate meals: 10-30 meals per day per restaurant
        const mealsPerDay = 10 + Math.floor(randomFactor * 20 + Math.random() * 10);
        const mealCount = mealsPerDay * dayCount;

        // Average meal price: 10-25 KM
        const avgMealPrice = 10 + randomFactor * 10 + Math.random() * 5;
        const total = mealCount * avgMealPrice;

        // Company pays 70-90%
        const companyPercent = 0.7 + randomFactor * 0.15 + Math.random() * 0.05;
        const companyAmount = total * companyPercent;
        const employeeAmount = total - companyAmount;

        summary.push({
          id: restaurantId,
          num: index + 1,
          restaurant: restaurant.name,
          meal_count: mealCount,
          total_amount: total,
          company_amount: companyAmount,
          employee_amount: employeeAmount,
          isTotal: false,
        });

        totalMeals += mealCount;
        totalAmount += total;
        totalCompanyAmount += companyAmount;
        totalEmployeeAmount += employeeAmount;
      });

      // Add total row
      if (summary.length > 0) {
        summary.push({
          id: "total",
          num: "",
          restaurant: this.$t("total").toUpperCase(),
          meal_count: totalMeals,
          total_amount: totalAmount,
          company_amount: totalCompanyAmount,
          employee_amount: totalEmployeeAmount,
          isTotal: true,
        });
      }

      return summary;
    },
    headers() {
      return [
        {
          text: this.$t("r_b"),
          value: "num",
          sortable: false,
          width: "80px",
          print: false,
        },
        { text: this.$t("restaurant"), value: "restaurant", sortable: false },
        {
          text: this.$t("meal_count"),
          value: "meal_count",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("amount") + ` ${this.restaurantCurrency}`,
          value: "total_amount",
          align: "right",
          sortable: false,
        },
        {
          text: this.$t("company_amount") + ` ${this.restaurantCurrency}`,
          value: "company_amount",
          align: "right",
          sortable: false,
        },
        {
          text: this.$t("employee_amount") + ` ${this.restaurantCurrency}`,
          value: "employee_amount",
          align: "right",
          sortable: false,
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("restaurant"), value: "restaurant" },
        { text: this.$t("meal_count"), value: "meal_count" },
        {
          text: this.$t("amount") + ` ${this.restaurantCurrency}`,
          value: "total_amount",
        },
        {
          text: this.$t("company_amount") + ` ${this.restaurantCurrency}`,
          value: "company_amount",
        },
        {
          text: this.$t("employee_amount") + ` ${this.restaurantCurrency}`,
          value: "employee_amount",
        },
      ];
    },
  },
  methods: {
    formatCurrency(amount) {
      return (parseFloat(amount) || 0).toFixed(2);
    },
    getExportHeaders() {
      return this.headers.filter((h) => h.print !== false);
    },
    formatSummaryForExport(item) {
      const moneyFields = ["total_amount", "company_amount", "employee_amount"];
      const exportItem = {};
      this.getExportHeaders().forEach((h) => {
        if (h.value === "restaurant") exportItem[h.value] = this.toLatin(item[h.value]);
        else if (moneyFields.includes(h.value))
          exportItem[h.value] = this.formatCurrency(item[h.value]);
        else exportItem[h.value] = item[h.value] || "";
      });
      return exportItem;
    },
  },
  watch: {
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("restaurant_summary")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("restaurant_summary"),
            headers: this.getExportHeaders(),
            items: this.restaurantSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            title: [this.$t("report"), this.$t("restaurant_summary")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      this.printDocument({
        tables: [
          {
            headers: this.getExportHeaders(),
            items: this.restaurantSummary.map((item) =>
              this.formatSummaryForExport(item)
            ),
            showCaption: true,
          },
        ],
        title: [this.$t("report"), this.$t("restaurant_summary")].join(": "),
        subtitle: this.subtitle,
        hideDatetime: true,
      });
    },
  },
};
</script>
